import {TranslateService} from "@ngx-translate/core";
import {OlapPivotResultsData, PivotResultsMemberDetailsData} from "../../framework/bi/olap-pivot-results.data";
import {MdxMemberUtil} from "../../framework/bi/mdx-member-util";
import {MeasureDataType} from "../../framework/bi/measure-data-type";

export class HeadingCellData {
    public label: string;

    /**
     * indicates that the cell spans and shares the label from the previous cells
     */
    public span: boolean;

    /**
     * Indicates that the cell is a blank spacer to align other cells
     */
    public spacer: boolean;

    /**
     * Indicates that the cell is a blank spacer that is below a label cell so forms an edge
     */
    public spacerEdge: boolean;

    /**
     * Cell is bottom of name
     */
    public bottom: boolean;

    /**
     * Indicates that the cell is a sub-total/total
     */
    public total: boolean;

    public name: string;

    /**
     * Original index from the OlapData
     */
    public index: number;

    /**
     * Members from high name dimensions
     */
    public parentMembers: string[];

    public isMeasure: boolean;

    /**
     * Data type for measures.
     * Currency or other
     */
    public measureDataType: MeasureDataType;

    /**
     * open - expanded
     * closed - not expanded
     * undefined - not expandable
     */
    public openStatus: string;

    /**
     * Indicates that there is no child data available
     */
    public noChildData: boolean;

    /**
     * Summary that is made up of all the parent members
     */
    public isSummary: boolean;

    /**
     * Populated if this cell is a measure on a summary
     */
    public measureName: string;
}

/**
 * OLAP data organised for pivot grid
 */
export class PivotData {

    public colHeadings: HeadingCellData[][];

    public rowHeadings: HeadingCellData[][];

    /**
     * Number of members that are included in the rows
     */
    public rowHeadingMemberCount: number;

    /**
     * Number of members that are open in the row heading. These are members that resolve to a member instead of a name or dimension
     */
    public openRowHeadingCount: number;

    /**
     * Number of parts in all the members that make up the column heading.
     * Using array so we can iterate in the template.
     */
    public columnHeadingDepth: number[];

    /**
     * Number of parts in all the members that make up the row heading.
     * Using array so we can iterate in the template.
     */
    public rowHeadingDepth: number[];

    /**
     *
     * @param {OlapData} olapPivotResultsData Original data that this PivotData is based on
     * @param {TranslateService} translateService
     */
    constructor(private olapPivotResultsData: OlapPivotResultsData, private translateService: TranslateService) {
        this.refresh();
    }

    private refresh(): void {
        this.colHeadings = [];
        this.columnHeadingDepth = [];
        this.prepareHeadings(this.olapPivotResultsData.columnHeadings, this.colHeadings, this.columnHeadingDepth);

        this.rowHeadings = [];
        this.rowHeadingDepth = [];
        this.prepareHeadings(this.olapPivotResultsData.rowHeadings, this.rowHeadings, this.rowHeadingDepth);

        if (this.olapPivotResultsData.rowHeadings && this.olapPivotResultsData.rowHeadings.length > 0) {
            this.rowHeadingMemberCount = this.olapPivotResultsData.rowHeadings[0].length;
            this.openRowHeadingCount = 0;
            this.olapPivotResultsData.rowHeadings[0].forEach((value: PivotResultsMemberDetailsData) => {
                if (MdxMemberUtil.isMember(value.name)) {
                    this.openRowHeadingCount++;
                }
            });

            this.rowHeadings[0].forEach((value: HeadingCellData, index: number) => {
                if (value.openStatus === "closed"
                    && !MdxMemberUtil.isMember(value.name)
                    && index < this.rowHeadings[0].length
                    && this.rowHeadings[0][index + 1].spacer) {
                    this.openRowHeadingCount++;
                }
            });
        }

        if (this.rowHeadings.length === 0 && this.olapPivotResultsData.values.length !== 0) {
            const preppedHeadings: HeadingCellData[] = [];
            const cell: HeadingCellData = new HeadingCellData();
            preppedHeadings.push(cell);
            this.rowHeadings.push(preppedHeadings);
        }
    }

    get values(): number[][] {
        return this.olapPivotResultsData.values;
    }

    get columnsTruncated(): number {
        return this.olapPivotResultsData.columnsTruncated;
    }

    get rowsTruncated(): number {
        return this.olapPivotResultsData.rowsTruncated;
    }

    private prepareHeadings(fromHeadings: PivotResultsMemberDetailsData[][], toHeadings: HeadingCellData[][], headingDepth: number[]) {

        let maxSize: number = 0;


        const openDimensionLevels: Set<string> = new Set();
        const closedDimensionLevels: Set<string> = new Set();
        const sizeForDimension: Map<string, number> = new Map();
        const totalsDimensionLevels: Set<string> = new Set();
        const internalToExternal: Map<string, string> = new Map();

        //
        // Find max name depth per dimension. This is needed to line up the levels
        //
        fromHeadings.forEach((headingMemberss: PivotResultsMemberDetailsData[]) => {
            headingMemberss.forEach((m: PivotResultsMemberDetailsData) => {
                const parts: string[] = MdxMemberUtil.getParts(m.name);
                if (parts.shift() !== "Measures") { // Dimension
                    parts.splice(1, 1); // Level
                }

                const dimKey = MdxMemberUtil.getDimensionKey(m.name);
                let dimensionSize: number = sizeForDimension.get(dimKey);
                if (!dimensionSize) {
                    dimensionSize = 0;
                }

                dimensionSize = Math.max(dimensionSize, parts.length);
                sizeForDimension.set(dimKey, dimensionSize);
            });
        });

        const lastMember: Map<number, string> = new Map();

        let measureAdded: boolean = false;
        //
        // Split the members into individual parts
        //
        fromHeadings.forEach((headingMembers: PivotResultsMemberDetailsData[]) => {
            const preppedHeadings: HeadingCellData[] = [];
            const parents: string[] = [];
            let parentMatched: boolean = false;
            let lastClosed: boolean = false;
            let lastTotal: boolean = false;
            headingMembers.forEach((m: PivotResultsMemberDetailsData, headingIndex: number) => {
                internalToExternal.set(m.name, m.external);

                const lastT = lastMember.get(headingIndex);
                const lastMatch = lastT && lastT === m.name;

                lastMember.set(headingIndex, m.name);

                let measure: boolean = false;
                let parts: string[] = MdxMemberUtil.getParts(m.external);
                parts = MdxMemberUtil.formatPartsForLabel(parts);
                if (parts.shift() !== "Measures") { // Dimension
                    parts.splice(1, 1); // Level
                } else {
                    measure = true;
                    measureAdded = true;
                }
                const dimKey = MdxMemberUtil.getDimensionKey(m.name);

                const parentsClone: string[] = parents.slice(0);

                let labelAdded: boolean = false;
                let lastCellAdded: HeadingCellData;
                parts.forEach((part: string, index: number) => {
                    const levelKey = dimKey + ";" + index;
                    let levelPartKey = levelKey + ";" + part;

                    if (parentsClone.length !== 0) {
                        levelPartKey = levelPartKey + this.buildKey(parentsClone);
                    }

                    const cell: HeadingCellData = new HeadingCellData();
                    lastCellAdded = cell;
                    cell.name = m.name;
                    cell.index = index;
                    cell.isMeasure = measure;
                    cell.measureDataType = m.dataType;
                    cell.bottom = true;
                    cell.total = lastTotal;

                    if (!lastMatch || (parts.length === 1 && (!parentMatched && parentsClone.length !== 0) || lastClosed)) {
                        cell.label = part;
                        if (!measure) {
                            if (index === parts.length - 1) {
                                if (m.hasChildren && !(openDimensionLevels.has(levelPartKey))) {
                                    cell.openStatus = "closed";
                                    closedDimensionLevels.add(levelPartKey);
                                    lastClosed = true;
                                }
                            } else {
                                if (openDimensionLevels.has(levelPartKey)) {
                                    cell.label = undefined;
                                    cell.span = true;
                                } else {
                                    cell.openStatus = "open";
                                    openDimensionLevels.add(levelPartKey);
                                    lastClosed = false;
                                }
                                cell.total = false;
                                lastTotal = false;
                            }
                        }

                        if (cell.label && index === 0 && !measure) {
                            // Translate top name
                            this.translateService.get("wbo.bi.dimension-key." + dimKey).subscribe((s: string) => {
                                cell.label = s;
                            });
                        }

                        cell.parentMembers = parentsClone;
                    } else {
                        cell.span = true;

                        if (totalsDimensionLevels.has(levelPartKey)) {
                            cell.total = true;
                            lastTotal = true;
                        } else if (!closedDimensionLevels.has(levelPartKey)) {
                            cell.total = false;
                            lastTotal = false;
                        }

                    }

                    if (cell.label) {
                        labelAdded = true;

                        if (!cell.openStatus && m.hasChildren) {
                            cell.total = true;
                            lastTotal = true;
                            totalsDimensionLevels.add(levelPartKey);
                        }

                    }

                    preppedHeadings.push(cell);
                });

                // Add empty cells to space out the parts so the start of the dimensions line up
                const dimensionSize = sizeForDimension.get(dimKey);
                if (dimensionSize) {
                    let len = parts.length;
                    if (len < dimensionSize && lastCellAdded) {
                        lastCellAdded.bottom = false;
                    }

                    while (len < dimensionSize) {
                        const cell: HeadingCellData = new HeadingCellData();
                        lastCellAdded = cell;
                        cell.spacerEdge = labelAdded;
                        cell.total = lastTotal;
                        cell.spacer = true;

                        preppedHeadings.push(cell);
                        len++;
                    }

                    if (lastCellAdded) {
                        lastCellAdded.bottom = true;
                    }
                }

                parents.push(m.name);

                parentMatched = lastMatch;

            });


            //
            // Add summary title cell
            //
            let summary: string = "";
            if (preppedHeadings.length > 0) {
                let h: HeadingCellData;
                let i = preppedHeadings.length - 1;
                while ((h = preppedHeadings[i]) && i > 0 && !h.parentMembers) {
                    i--;
                }

                let measureName: string = undefined;
                if (h.parentMembers) {
                    h.parentMembers.forEach((t: string) => {
                        const parts: string[] = MdxMemberUtil.formatPartsForLabel(MdxMemberUtil.getParts(internalToExternal.get(t)));

                        if (parts[0] === "Measures") {
                            measureName = parts[parts.length - 1];
                        } else if (parts.length > 2) {
                            if (summary.length !== 0) {
                                summary = summary + " / ";
                            }
                            summary = summary + parts[parts.length - 1];
                        }
                    });
                }
                if (h.name) {
                    const parts: string[] = MdxMemberUtil.formatPartsForLabel(MdxMemberUtil.getParts(internalToExternal.get(h.name)));
                    if (parts[0] === "Measures") {
                        measureName = parts[parts.length - 1];
                    } else if (parts.length > 2) {
                        if (summary.length !== 0) {
                            summary = summary + " / ";
                        }
                        summary = summary + parts[parts.length - 1];
                    }
                }
                if (summary.length === 0 && preppedHeadings[0].label && !measureName) {
                    summary = preppedHeadings[0].label;
                }

                const summaryHeading: HeadingCellData = new HeadingCellData();
                summaryHeading.label = summary;
                summaryHeading.isSummary = true;
                summaryHeading.total = preppedHeadings[preppedHeadings.length - 1].total;
                summaryHeading.measureDataType = preppedHeadings[preppedHeadings.length - 1].measureDataType;
                summaryHeading.measureName = measureName;

                preppedHeadings.push(summaryHeading);
            }

            maxSize = Math.max(maxSize, preppedHeadings.length);
            toHeadings.push(preppedHeadings);
        });

        // if (!this.showDimension && fromHeadings.length > 0) {
        //     maxSize = maxSize - fromHeadings.length - 1;
        // }

        // Populate the depth
        for (let i = 0; i < maxSize; i++) {
            headingDepth.push(i);
        }
    }

    /**
     * Indicates if a column is for a sub-total value
     * @param {number} colIdx Header column index
     */
    public isTotalColumn(colIdx: number): boolean {
        return this.colHeadings[colIdx][this.colHeadings[colIdx].length - 1].total;
    }

    public isCurrency(colIdx: number): boolean {
        return MeasureDataType.CURRENCY === this.colHeadings[colIdx][this.colHeadings[colIdx].length - 1].measureDataType;
    }

    public isPercentage(colIdx: number): boolean {
        return MeasureDataType.PERCENTAGE === this.colHeadings[colIdx][this.colHeadings[colIdx].length - 1].measureDataType ||
            MeasureDataType.CALCULATED_PERCENTAGE === this.colHeadings[colIdx][this.colHeadings[colIdx].length - 1].measureDataType
    }
    public getMeasureType(colIdx: number): MeasureDataType {
        return this.colHeadings[colIdx][this.colHeadings[colIdx].length - 1].measureDataType;
    }


    /**
     * Indicates if a row is for a sub-total value
     * @param {number} rowIdx Header column index
     */
    public isTotalRow(rowIdx: number): boolean {
        return this.rowHeadings[rowIdx][this.rowHeadings[rowIdx].length - 1].total;
    }

    public mergeDrillOnColumn(newColumns: OlapPivotResultsData, colHeading: HeadingCellData): void {

        const valueMap: Map<string, number> = this.buildValueMap(newColumns);

        let startIdx: number = 0;
        this.olapPivotResultsData.columnHeadings.some((h: PivotResultsMemberDetailsData[], index: number) => {
            if (this.match(h, newColumns.columnHeadings[0], colHeading.parentMembers.length + 1)) {
                startIdx = index;
                return true;
            }
            return false;
        });


        newColumns.columnHeadings.forEach((h: PivotResultsMemberDetailsData[], index: number) => {
            this.olapPivotResultsData.columnHeadings.splice(startIdx + index, 0, h);
        });

        const endIdx = startIdx + newColumns.columnHeadings.length;
        for (let colIdx: number = startIdx; colIdx < endIdx; colIdx++) {
            const colValues: number[] = [];
            this.olapPivotResultsData.values.splice(colIdx, 0, colValues);
            if (this.olapPivotResultsData.rowHeadings.length === 0) {
                const value: number = valueMap.get(this.getCoordKey(this.olapPivotResultsData.columnHeadings[colIdx], null));
                colValues.push(value);
            } else {
                this.olapPivotResultsData.rowHeadings.forEach((rows: PivotResultsMemberDetailsData[]) => {
                    const value: number = valueMap.get(this.getCoordKey(this.olapPivotResultsData.columnHeadings[colIdx], rows));
                    colValues.push(value);
                });
            }
        }


        this.refresh();
    }

    public mergeDrillOnRow(newRows: OlapPivotResultsData, rowHeading: HeadingCellData): void {

        const valueMap: Map<string, number> = this.buildValueMap(newRows);

        let startIdx: number = 0;
        this.olapPivotResultsData.rowHeadings.some((h: PivotResultsMemberDetailsData[], index: number) => {
            if (this.match(h, newRows.rowHeadings[0], rowHeading.parentMembers.length + 1)) {
                startIdx = index;
                return true;
            }
            return false;
        });


        newRows.rowHeadings.forEach((h: PivotResultsMemberDetailsData[], index: number) => {
            this.olapPivotResultsData.rowHeadings.splice(startIdx + index, 0, h);
        });

        const endIdx = startIdx + newRows.rowHeadings.length;
        for (let rowIdx: number = startIdx; rowIdx < endIdx; rowIdx++) {
            this.olapPivotResultsData.columnHeadings.forEach((col: PivotResultsMemberDetailsData[], colIdx: number) => {
                const value: number = valueMap.get(this.getCoordKey(col, this.olapPivotResultsData.rowHeadings[rowIdx]));
                this.olapPivotResultsData.values[colIdx].splice(rowIdx, 0, value);

            });
        }

        this.refresh();
    }

    /**
     * Remove all columns that are children of the specified name
     */
    public removeColumnChildren(name: string, parentMembers: string[]) {
        const memberPrefix = name + ".";

        const parentKey: string = this.buildKey(parentMembers);

        for (let i = this.olapPivotResultsData.columnHeadings.length - 1; i >= 0; i--) {

            let found: boolean = false;
            this.olapPivotResultsData.columnHeadings[i].forEach((t: PivotResultsMemberDetailsData, i2: number) => {
                if (t.name.startsWith(memberPrefix) && parentKey === this.buildMemberDetailsKey(this.olapPivotResultsData.columnHeadings[i], i2)) {
                    found = true;
                }
            });

            if (found) {
                this.olapPivotResultsData.columnHeadings.splice(i, 1);
                this.olapPivotResultsData.values.splice(i, 1);
            }
        }

        this.refresh();
    }

    /**
     * Remove all rows that are children of the specified name
     */
    public removeRowChildren(name: string, parentMembers: string[]) {
        const memberPrefix = name + ".";

        const parentKey: string = this.buildKey(parentMembers);

        for (let i = this.olapPivotResultsData.rowHeadings.length - 1; i >= 0; i--) {

            let found: boolean = false;
            this.olapPivotResultsData.rowHeadings[i].forEach((t: PivotResultsMemberDetailsData, i2: number) => {
                if (t.name.startsWith(memberPrefix) && parentKey === this.buildMemberDetailsKey(this.olapPivotResultsData.rowHeadings[i], i2)) {
                    found = true;
                }
            });

            if (found) {
                this.olapPivotResultsData.rowHeadings.splice(i, 1);
                this.olapPivotResultsData.values.forEach((colValues: number[]) => {
                    colValues.splice(i, 1);
                });
            }
        }

        this.refresh();
    }

    /**
     * Builds a map of values indexed by coord keys
     * @param {OlapData} data
     * @returns {Map<string, number>}
     */
    private buildValueMap(data: OlapPivotResultsData): Map<string, number> {
        const valueMap: Map<string, number> = new Map();
        data.columnHeadings.forEach((col: PivotResultsMemberDetailsData[], colIndx: number) => {
            if (data.rowHeadings.length === 0) {
                // no rows
                valueMap.set(this.getCoordKey(col, null), data.values[colIndx][0]);
            } else {
                data.rowHeadings.forEach((rows: PivotResultsMemberDetailsData[], rowIndx: number) => {
                    valueMap.set(this.getCoordKey(col, rows), data.values[colIndx][rowIndx]);
                });
            }
        });

        return valueMap;
    }

    /**
     * Derives a key from the column and row members
     * @param {string[]} col
     * @param {string[]} rows
     * @returns {string}
     */
    private getCoordKey(col: PivotResultsMemberDetailsData[], rows: PivotResultsMemberDetailsData[]): string {

        let k: string = "";
        col.forEach((t: PivotResultsMemberDetailsData) => {
            k = k + t.name;
        });

        if (rows) {
            rows.forEach((t: PivotResultsMemberDetailsData) => {
                k = k + t.name;
            });
        }

        return k;
    }


    /**
     * Checks if 2 name lists match. They match if every name in name 2 starts with name 1.
     * I.e. name 2 may be deeper.
     * @param {string[]} m1
     * @param {string[]} m2
     * @param depth Optional depth to check to
     * @returns {boolean}
     */
    private match(m1: PivotResultsMemberDetailsData[], m2: PivotResultsMemberDetailsData[], depth?: number): boolean {
        if (m1.length !== m2.length) {
            return false;
        }

        if (!depth) {
            depth = m1.length;
        }

        let match = true;
        m1.forEach((m: PivotResultsMemberDetailsData, index: number) => {
            if (index < depth && !m2[index].name.startsWith(m.name)) {
                match = false;
            }
        });

        return match;
    }

    private buildKey(members: string[]): string {
        if (!members) {
            return "";
        }

        let k: string = "";
        members.forEach((t: string) => {
            k = k + ";" + t;
        });
        return k;
    }

    private buildMemberDetailsKey(memberDetails: PivotResultsMemberDetailsData[], depth: number): string {
        if (!memberDetails || !depth) {
            return "";
        }


        let k: string = "";
        memberDetails.forEach((t: PivotResultsMemberDetailsData, index: number) => {
            if (index < depth) {
                k = k + ";" + t.name;
            }
        });
        return k;
    }

}
